/**
 * 시딩 mutation(신청/수락/거절/취소/콘텐츠 제출/배송지 수정) 실패 시 사용자에게 노출할 메시지를
 * 도출한다. service/api 레이어가 던지는 에러는 두 형태로 오므로 양쪽 모두에서 BE message 를 살린다.
 *
 * - non-2xx HTTP: `executeStandardRequest` 가 `StandardResponseErrorModel`(`{data,message,status}`)
 *   평문 객체로 `Promise.reject`. 이때 BE 실제 메시지는 **응답 body 의 `meta.message`** 에 담기고,
 *   변환된 객체에서는 `error.data.meta.message` 경로다. (top-level `error.message` 는
 *   "서버와의 통신에 실패했습니다." 같은 generic 기본값이라 신뢰할 수 없다.)
 *   → 코드베이스 공통 규약(`isApiError` 의 `error.data.meta.message`)과 동일한 경로를 본다.
 * - 200 + `meta.result === 'FAIL'`: service 가 `new Error(meta.message)` 로 throw → `Error` 인스턴스의
 *   `message` 에 이미 BE 메시지가 들어 있다.
 *
 * 둘 다 비어 있으면 호출부가 넘긴 `fallback` 을 사용한다.
 *
 * NOTE: `@musinsa/services-curator` 의 `isApiError` 와 동일한 판정이지만, 순수 util 이 무거운 service
 * barrel(transitive ESM) 에 결합되지 않도록 동일 shape 의 가드를 인라인한다.
 */
const readApiErrorMetaMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null || !('data' in error)) return undefined;
  const { data } = error as { data?: unknown };
  if (typeof data !== 'object' || data === null || !('meta' in data)) return undefined;
  const { meta } = data as { meta?: unknown };
  if (typeof meta !== 'object' || meta === null || !('message' in meta)) return undefined;
  const { message } = meta as { message?: unknown };
  return typeof message === 'string' && message ? message : undefined;
};

export const resolveMutationErrorMessage = (error: unknown, fallback: string): string => {
  // 1. non-2xx — BE 표준 응답 meta.message (응답 body) 를 최우선으로 노출.
  const metaMessage = readApiErrorMetaMessage(error);
  if (metaMessage) return metaMessage;

  // 2. 200 + FAIL — service 가 던진 Error.message (= meta.message).
  if (error instanceof Error && error.message) return error.message;

  return fallback;
};
